/* eslint-disable react/prefer-stateless-function */
import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Redirect } from 'react-router'
import classNames from 'classnames'
import { Helmet } from 'react-helmet-async'
import { INTERNAL_SERVER_ERROR } from '../../server/lib/http-codes'
import ErrorFeature from '../components/error-feature'
import Spinner from '../components/spinner'
import { getDisplayName } from '../helpers/get-display-name'
import canonicalLink from '../helpers/canonical-link'
import { useEvolvedContext, useGetHrefContext } from '../context'
import ComponentMetaData from '../components/component-meta-data'
import DataLayer from '../components/data-layer'
import ErrorPage from './error'

const isValidMeta = meta =>
  meta.canonical ||
  meta.title ||
  meta.description ||
  meta.schema ||
  meta.openGraphData

const pagesHoc = (
  WrappedComponent,
  getMetaData,
  canonicalizeUrl = canonicalLink.canonical
) => {
  const NewComponent = props => {
    const isEvolved = useEvolvedContext()
    const getUrl = useGetHrefContext()
    const { centre, error, loading, redirect, brand } = props

    if (error) {
      return centre ? (
        <ErrorFeature
          status={error.code || INTERNAL_SERVER_ERROR}
          error={error}
        />
      ) : (
        <ErrorPage status={error.code} error={error} />
      )
    }

    if (loading) {
      // Loading page
      return <Spinner className={classNames({ brand })} />
    }

    if (redirect) {
      return <Redirect to={redirect} />
    }

    const canonical = canonicalizeUrl(getUrl(), { ...props, isEvolved })

    const meta =
      getMetaData?.({
        ...props,
        canonical,
      }) ?? {}

    return (
      <Fragment>
        {!isEvolved && isValidMeta(meta) && (
          <Fragment>
            <Helmet>
              {meta.title && <title>{meta.title}</title>}
              {meta.description && (
                <meta
                  key="description"
                  name="description"
                  content={meta.description}
                />
              )}
              {canonical && (
                <link key="canonical" rel="canonical" href={canonical} />
              )}
            </Helmet>
            <ComponentMetaData
              data={{
                structuredData: meta.schema,
                openGraphData: meta.openGraphData,
              }}
            />
          </Fragment>
        )}
        <DataLayer {...meta.datalayer} />
        <WrappedComponent {...props} />
      </Fragment>
    )
  }
  NewComponent.displayName = `Page(${getDisplayName(WrappedComponent)})`
  NewComponent.propTypes = {
    error: PropTypes.object,
    loading: PropTypes.bool,
    redirect: PropTypes.string,
    centre: PropTypes.object,
    brand: PropTypes.bool,
  }
  return NewComponent
}

export default pagesHoc
